import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Column } from "devextreme-react/data-grid";
import { FilterTabs, MasterGrid, MasterPage, OpenAction, OPEN_COLUMN } from "../Shared/master";
import DateRangePicker from "../Shared/DateRangePicker";
import useAbortSignal from "../../hooks/useAbortSignal";
import { isRequestCancelled } from "../../apis/useAxios";
import { showErrorToast } from "../../helpers/message";
import { usePermission } from "../../helpers/permissions";
import Stars from "./Stars";
import RatingDrawer from "./RatingDrawer";
import {
  MENU_CODE,
  RANGE_PRESETS,
  RATING_REPLY_ENABLED,
  defaultRange,
  filterRows,
  formatDateTime,
  isReplied,
  overallScore,
  typeLabel,
  visibleFilters,
} from "./ratingRules";
import { getRatings } from "./ratingService";
import "./rating.css";

const renderScore = ({ data }) => <Stars score={overallScore(data)} showValue />;
const renderReplied = ({ data }) =>
  isReplied(data) ? <span className="mst-status is-active">Replied</span> : <span className="mst-status is-inactive">Not replied</span>;
const renderCrew = ({ data }) => (
  <span>
    {data.crewName || "-"}
    {data.employeeNumber && <span className="text-muted"> ({data.employeeNumber})</span>}
  </span>
);

/**
 * Crew Feedback › Rating: the ratings crew give in the crew app for hotels, hotel shuttles and local vendors, in a date
 * range. Open a rating to see its scores and comment. Staff reply is switched off for now (RATING_REPLY_ENABLED).
 */
export function RatingComponent() {
  const getSignal = useAbortSignal();
  const { edit: canEdit } = usePermission(MENU_CODE);
  const [draft, setDraft] = useState(defaultRange);
  const [range, setRange] = useState(draft);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    const [from, to] = range;
    if (!from || !to) return;
    setLoading(true);
    try {
      setRows(await getRatings({ from, to }, { signal: getSignal() }));
    } catch (err) {
      if (!isRequestCancelled(err)) showErrorToast(err?.message);
    } finally {
      setLoading(false);
    }
  }, [range, getSignal]);

  useEffect(() => {
    load();
  }, [load]);

  const filters = useMemo(() => visibleFilters(), []);
  const counts = useMemo(() => Object.fromEntries(filters.map((f) => [f.id, filterRows(rows, f.id).length])), [filters, rows]);
  const shown = useMemo(() => filterRows(rows, filter), [rows, filter]);
  const average = useMemo(() => {
    const scored = shown.map(overallScore).filter(Boolean);
    return scored.length ? Math.round((scored.reduce((a, b) => a + b, 0) / scored.length) * 10) / 10 : 0;
  }, [shown]);
  const selected = rows.find((r) => r.id === selectedId) ?? null;

  const renderOpen = useCallback(
    ({ data }) => (
      <OpenAction
        onClick={() => setSelectedId(data.id)}
        title={RATING_REPLY_ENABLED ? "Open rating (scores, comment, reply)" : "Open rating (scores, comment)"}
      />
    ),
    [],
  );

  return (
    <MasterPage title="Rating">
      <section className="mst-filter-panel" aria-label="Filters">
        <FilterTabs
          wide
          ariaLabel="Filter ratings"
          items={filters.map((f) => ({ ...f, count: counts[f.id] }))}
          value={filter}
          total={counts.all}
          onChange={setFilter}
        />
        <div className="mst-filter-panel__bar">
          <div className="mst-filter-panel__field">
            <label className="mst-filter-panel__label">Rated from - to</label>
            <div className="mst-filter-panel__fields">
              <DateRangePicker
                startDate={draft[0]}
                endDate={draft[1]}
                presets={RANGE_PRESETS}
                isClearable={false}
                ariaLabel="Rated from - to"
                onChange={(next) => setDraft(next)}
              />
              {/* A new array always reloads, so Search on the same range refreshes it. */}
              <button
                type="button"
                className="btn btn-primary btn-sm mst-filter-panel__search"
                disabled={loading || !draft[0] || !draft[1]}
                onClick={() => setRange([...draft])}
              >
                <i className={`fas fa-${loading ? "spinner fa-spin" : "search"}`} aria-hidden="true" /> Search
              </button>
            </div>
          </div>
          <p className="mst-filter-panel__summary" aria-live="polite">
            Average of the ratings shown: <Stars score={average} showValue />
            {!average && <span className="text-muted"> none</span>}
          </p>
        </div>
      </section>

      <MasterGrid dataSource={shown} keyExpr="id" loading={loading} onRefresh={load} exportFileName="Ratings" allowColumnChooser={false}>
        <Column {...OPEN_COLUMN} cellRender={renderOpen} />
        <Column
          dataField="dateOfRating"
          caption="Rated on"
          dataType="datetime"
          width={150}
          calculateDisplayValue={(r) => formatDateTime(r.dateOfRating)}
          sortOrder="desc"
        />
        <Column caption="Type" width={130} calculateCellValue={typeLabel} />
        <Column dataField="placeName" caption="Hotel / vendor" />
        <Column dataField="airportCode" caption="Airport" width={95} />
        <Column caption="Crew" calculateCellValue={(r) => `${r.crewName} ${r.employeeNumber}`} cellRender={renderCrew} />
        <Column caption="Score" width={150} calculateCellValue={overallScore} cellRender={renderScore} />
        <Column dataField="comments" caption="Comment" />
        <Column
          caption="Reply"
          visible={RATING_REPLY_ENABLED}
          width={120}
          alignment="center"
          calculateCellValue={(r) => (isReplied(r) ? "Replied" : "Not replied")}
          cellRender={renderReplied}
        />
      </MasterGrid>

      {selected && <RatingDrawer key={selected.id} rating={selected} canEdit={canEdit} onChanged={load} onClose={() => setSelectedId(null)} />}
    </MasterPage>
  );
}

export default RatingComponent;
